import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckSquare, Plus, Calendar, AlertTriangle, CheckCircle, Clock, Trash2, Building2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ['email', 'call', 'meeting', 'note', 'task'];
const PRIORITIES = ['low', 'medium', 'high'];
const TYPE_ICONS = { email: '📧', call: '📞', meeting: '📅', note: '📝', task: '✅' };
const TYPE_COLORS = { email: 'bg-blue-100 text-blue-600', call: 'bg-green-100 text-green-600', meeting: 'bg-amber-100 text-amber-600', note: 'bg-purple-100 text-purple-600', task: 'bg-stone-100 text-stone-700' };

function ActivityModal({ open, onClose, accounts, onSave }) {
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState({ type: 'task', title: '', description: '', account_id: '', due_date: '', priority: 'medium', status: 'planned', outcome: '' });
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        if (!form.title) return toast.error('Title required');
        setLoading(true);
        try {
            const res = await axios.post(`${API}/activities`, form, { headers: getAuthHeader() });
            toast.success('Activity created');
            onSave(res.data);
            onClose();
            setForm({ type: 'task', title: '', description: '', account_id: '', due_date: '', priority: 'medium', status: 'planned', outcome: '' });
        } catch { toast.error('Failed to create'); }
        finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Log Activity</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Type</label>
                            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} data-testid="activity-type-select"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none capitalize">
                                {TYPES.map(t => <option key={t} className="capitalize">{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Priority</label>
                            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="activity-priority-select"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none capitalize">
                                {PRIORITIES.map(p => <option key={p} className="capitalize">{p}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Title *</label>
                        <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Follow-up call with Sarah"
                            data-testid="activity-title-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company</label>
                        <select value={form.account_id || ''} onChange={e => setForm({ ...form, account_id: e.target.value })} data-testid="activity-account-select"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                            <option value="">Select account...</option>
                            {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Due Date</label>
                            <input type="date" value={form.due_date || ''} onChange={e => setForm({ ...form, due_date: e.target.value })}
                                data-testid="activity-due-date-input"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Status</label>
                            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none capitalize">
                                {['planned', 'completed', 'cancelled'].map(s => <option key={s} className="capitalize">{s}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Description / Notes</label>
                        <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none resize-none" />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                    <button onClick={handleSave} disabled={loading} data-testid="activity-save-btn"
                        className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                        {loading ? 'Saving...' : 'Log Activity'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function Activities() {
    const { getAuthHeader } = useAuth();
    const [activities, setActivities] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [filter, setFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [aRes, accRes] = await Promise.all([
                axios.get(`${API}/activities`, { headers: getAuthHeader() }),
                axios.get(`${API}/accounts`, { headers: getAuthHeader() }),
            ]);
            setActivities(Array.isArray(aRes.data) ? aRes.data : (aRes.data?.data || []));
            setAccounts(Array.isArray(accRes.data) ? accRes.data : (accRes.data?.data || []));
        } catch {} finally { setLoading(false); }
    };

    const handleComplete = async (id) => {
        try {
            const res = await axios.put(`${API}/activities/${id}`, { status: 'completed' }, { headers: getAuthHeader() });
            setActivities(prev => prev.map(a => a.activity_id === id ? res.data : a));
            toast.success('Activity completed');
        } catch { toast.error('Failed to update'); }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API}/activities/${id}`, { headers: getAuthHeader() });
            setActivities(prev => prev.filter(a => a.activity_id !== id));
            toast.success('Activity deleted');
        } catch { toast.error('Failed to delete'); }
    };

    const handleSave = (saved) => setActivities(prev => [saved, ...prev]);

    const getAccountName = (id) => accounts.find(a => a.account_id === id)?.name;

    const isOverdue = (a) => a.status === 'planned' && a.due_date && a.due_date < new Date().toISOString().split('T')[0];

    let filtered = activities;
    if (filter === 'planned') filtered = filtered.filter(a => a.status === 'planned');
    if (filter === 'completed') filtered = filtered.filter(a => a.status === 'completed');
    if (filter === 'overdue') filtered = filtered.filter(a => isOverdue(a));
    if (typeFilter !== 'all') filtered = filtered.filter(a => a.type === typeFilter);

    const stats = {
        total: activities.length,
        planned: activities.filter(a => a.status === 'planned').length,
        completed: activities.filter(a => a.status === 'completed').length,
        overdue: activities.filter(a => isOverdue(a)).length,
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Total Activities', value: stats.total, color: 'text-stone-700', onClick: () => setFilter('all') },
                    { label: 'Planned', value: stats.planned, color: 'text-blue-600', onClick: () => setFilter('planned') },
                    { label: 'Completed', value: stats.completed, color: 'text-emerald-600', onClick: () => setFilter('completed') },
                    { label: 'Overdue', value: stats.overdue, color: 'text-red-600', onClick: () => setFilter('overdue') },
                ].map((m, i) => (
                    <div key={i} className="bg-white rounded-xl border border-stone-200 p-5 cursor-pointer hover:border-stone-300 card-hover" onClick={m.onClick}>
                        <p className={`text-2xl font-bold ${m.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                        <p className="text-xs text-stone-500 mt-0.5">{m.label}</p>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex gap-2 flex-wrap">
                    {['all', 'planned', 'completed', 'overdue'].map(f => (
                        <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                            className={`px-3 py-1.5 text-sm rounded-lg capitalize transition-colors ${filter === f ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                            {f}
                        </button>
                    ))}
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                        className="px-3 py-1.5 text-sm rounded-lg bg-white border border-stone-200 text-stone-600 focus:outline-none capitalize">
                        <option value="all">All Types</option>
                        {TYPES.map(t => <option key={t} className="capitalize">{t}</option>)}
                    </select>
                </div>
                <button onClick={() => setShowModal(true)} data-testid="log-activity-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 shadow-sm">
                    <Plus className="w-4 h-4" /> Log Activity
                </button>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="activities-list">
                {loading ? <div className="p-12 text-center text-stone-400">Loading...</div> :
                filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <CheckSquare className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500 text-sm">No activities</p>
                    </div>
                ) : (
                    <div className="divide-y divide-stone-100">
                        {filtered.map(a => {
                            const overdue = isOverdue(a);
                            return (
                                <div key={a.activity_id} className={`flex items-center gap-4 px-5 py-4 hover:bg-stone-50 group transition-colors ${overdue ? 'bg-red-50/30' : ''}`} data-testid={`activity-${a.activity_id}`}>
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-base flex-shrink-0 ${TYPE_COLORS[a.type] || 'bg-stone-100 text-stone-600'}`}>
                                        {TYPE_ICONS[a.type]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium text-stone-900">{a.title}</p>
                                            {overdue && <span className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Overdue</span>}
                                        </div>
                                        <div className="flex items-center gap-3 mt-0.5">
                                            {a.account_id && <span className="text-xs text-stone-400 flex items-center gap-1"><Building2 className="w-3 h-3" />{getAccountName(a.account_id)}</span>}
                                            {a.due_date && <span className="text-xs text-stone-400 flex items-center gap-1"><Calendar className="w-3 h-3" />{a.due_date}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${a.priority === 'high' ? 'bg-red-50 text-red-600' : a.priority === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-stone-50 text-stone-500'}`}>{a.priority}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${a.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{a.status}</span>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {a.status !== 'completed' && (
                                            <button onClick={() => handleComplete(a.activity_id)} data-testid={`complete-${a.activity_id}`}
                                                className="p-1.5 rounded-lg hover:bg-emerald-50 text-stone-400 hover:text-emerald-600">
                                                <CheckCircle className="w-4 h-4" />
                                            </button>
                                        )}
                                        <button onClick={() => handleDelete(a.activity_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ActivityModal open={showModal} onClose={() => setShowModal(false)} accounts={accounts} onSave={handleSave} />
        </div>
    );
}
